import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import { collection, query, where, getDocs, limit } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { PLANNER_PAGES, IMAGES, PRODUCTS, formatINR } from "@/lib/products";
import { PlannerPagePreview } from "@/components/product/PlannerPagePreview";
import { Progress } from "@/components/ui/progress";
import { LayoutDashboard, BookOpen, Cloud, Package, Heart, Download, Bookmark, User as UserIcon, LogOut, TrendingUp, Target, Flame, ChevronRight, Play } from "lucide-react";

const SIDEBAR = [
  { key: "overview", label: "Overview", icon: LayoutDashboard, to: "/dashboard" },
  { key: "planner", label: "My Planner", icon: BookOpen, to: "/dashboard" },
  { key: "digital", label: "Digital Companion", icon: Cloud, to: "/dashboard" },
  { key: "orders", label: "Orders", icon: Package, to: "/orders" },
  { key: "wishlist", label: "Wishlist", icon: Heart, to: "/dashboard" },
  { key: "downloads", label: "Downloads", icon: Download, to: "/dashboard" },
  { key: "saved", label: "Saved Pages", icon: Bookmark, to: "/dashboard" },
  { key: "profile", label: "Profile", icon: UserIcon, to: "/dashboard" },
];

export default function Dashboard() {
  const { user, loading, logout } = useAuth();
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const email = (user.email || "").trim().toLowerCase();
        const queries = [
          query(collection(db, "orders"), where("userId", "==", user.uid), limit(3)),
          ...(email
            ? [
                query(collection(db, "orders"), where("emailLower", "==", email), limit(3)),
                query(collection(db, "orders"), where("email", "==", user.email), limit(3)),
              ]
            : []),
        ];
        const snaps = await Promise.all(queries.map((orderQuery) => getDocs(orderQuery)));
        const byId = new Map();
        snaps.forEach((snap) => {
          snap.docs.forEach((d) => byId.set(d.id, { id: d.id, ...d.data() }));
        });
        const list = Array.from(byId.values()).sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        setRecent(list.slice(0, 1));
      } catch (e) {
        // Ignore missing index errors gracefully
      }
    })();
  }, [user]);

  if (loading) return <div className="container-nc py-20 text-muted_brown">Loading...</div>;
  if (!user) return <Navigate to="/login" replace/>;

  const firstName = user.displayName?.split(" ")[0] || user.email?.split("@")[0] || "friend";

  return (
    <div data-testid="dashboard-page" className="container-nc py-12">
      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="bg-ivory border border-line rounded-xl p-5">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-full bg-champagne flex items-center justify-center text-espresso font-serif text-lg">
                {firstName[0]?.toUpperCase()}
              </div>
              <div>
                <div className="text-espresso font-medium text-sm truncate max-w-[140px]">{user.displayName || firstName}</div>
                <div className="text-xs text-muted_brown truncate max-w-[140px]">{user.email}</div>
              </div>
            </div>
            <nav className="space-y-1">
              {SIDEBAR.map(({ key, label, icon: Icon, to }) => (
                <Link
                  key={key}
                  to={to}
                  data-testid={`side-${key}`}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted_brown hover:bg-cream hover:text-espresso transition-colors"
                >
                  <Icon size={15} strokeWidth={1.5}/>
                  <span>{label}</span>
                </Link>
              ))}
              <button
                onClick={logout}
                data-testid="side-logout"
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted_brown hover:bg-cream hover:text-espresso transition-colors"
              >
                <LogOut size={15} strokeWidth={1.5}/> Logout
              </button>
            </nav>
          </div>
        </aside>

        {/* Main */}
        <div>
          <p className="overline mb-2">Dashboard</p>
          <h1 className="font-serif text-4xl md:text-5xl text-espresso">Welcome back, {firstName}.</h1>
          <p className="text-muted_brown mt-2">Here's what's happening in your planning journey.</p>

          {/* Metric cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            {[
              { icon: TrendingUp, label: "Planner Progress", value: "38%", extra: <Progress value={38} className="mt-3 h-1"/> },
              { icon: BookOpen, label: "Pages Completed", value: "82" },
              { icon: Flame, label: "Current Streak", value: "14 days" },
              { icon: Target, label: "Goals in Progress", value: "5" },
            ].map((m) => (
              <div key={m.label} className="bg-cream border border-line rounded-xl p-5">
                <div className="flex items-center gap-2 text-muted_brown text-xs uppercase tracking-wider">
                  <m.icon size={13} strokeWidth={1.5}/> {m.label}
                </div>
                <div className="font-serif text-3xl text-espresso mt-2">{m.value}</div>
                {m.extra}
              </div>
            ))}
          </div>

          {/* Recent order */}
          <div className="mt-10 bg-ivory border border-line rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-2xl text-espresso">Recent Order</h3>
              <Link to="/orders" className="text-sm text-espresso underline">View all</Link>
            </div>
            {recent.length > 0 ? (
              <div className="flex items-center gap-4">
                <img src={IMAGES.planner_closed} alt="" className="w-16 h-16 rounded-lg object-cover"/>
                <div className="flex-1">
                  <div className="text-espresso font-medium">Order #{recent[0].id.slice(0, 6).toUpperCase()}</div>
                  <div className="text-xs text-muted_brown">Status: {recent[0].status} · {formatINR(recent[0].total || 0)}</div>
                </div>
                <Link to="/orders" className="btn-outline text-xs py-2 px-4">Track</Link>
              </div>
            ) : (
              <p className="text-sm text-muted_brown">No orders yet. <Link to="/shop" className="underline text-espresso">Start shopping</Link>.</p>
            )}
          </div>

          {/* Quick actions */}
          <div className="mt-10">
            <h3 className="font-serif text-2xl text-espresso mb-5">Quick Actions</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { label: "Open My Planner", icon: BookOpen },
                { label: "Download Resources", icon: Download },
                { label: "Track My Goals", icon: Target },
                { label: "Browse Templates", icon: LayoutDashboard },
                { label: "View Tutorials", icon: Play },
                { label: "Contact Support", icon: UserIcon },
              ].map(({ label, icon: Icon }) => (
                <button key={label} data-testid={`quick-${label.replace(/ /g,'-').toLowerCase()}`} className="text-left p-5 bg-cream border border-line rounded-xl hover:shadow-hover transition-shadow group">
                  <Icon size={18} strokeWidth={1.5} className="text-gold mb-3"/>
                  <div className="text-espresso font-medium text-sm">{label}</div>
                  <div className="text-xs text-muted_brown mt-1 flex items-center gap-1">Open <ChevronRight size={12} className="group-hover:translate-x-0.5 transition-transform"/></div>
                </button>
              ))}
            </div>
          </div>

          {/* Saved pages + Recommendations */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-10">
            <div className="bg-cream border border-line rounded-xl p-6">
              <h3 className="font-serif text-2xl text-espresso mb-4">Saved Pages</h3>
              <div className="grid grid-cols-3 gap-3">
                {PLANNER_PAGES.slice(0, 3).map((p) => (
                  <div key={p.key}>
                    <div className="aspect-[3/4] rounded-lg overflow-hidden border border-line">
                      <PlannerPagePreview page={p} />
                    </div>
                    <div className="text-xs text-espresso mt-2">{p.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-champagne/40 border border-line rounded-xl p-6">
              <h3 className="font-serif text-2xl text-espresso mb-2">Motivational</h3>
              <p className="text-muted_brown font-serif italic text-lg leading-relaxed">
                "Discipline is choosing between what you want now and what you want most."
              </p>
              <div className="text-xs text-muted_brown mt-4">— Abraham Lincoln</div>
            </div>
          </div>

          {/* Recommended */}
          <div className="mt-12">
            <h3 className="font-serif text-2xl text-espresso mb-5">Recommended for you</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {PRODUCTS.slice(0, 4).map((p) => (
                <Link key={p.id} to={`/product/${p.slug}`} className="group">
                  <div className="aspect-square rounded-lg overflow-hidden border border-line bg-ivory">
                    <img src={p.images[0]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                  </div>
                  <div className="text-sm text-espresso mt-2">{p.name}</div>
                  <div className="text-xs text-muted_brown">{formatINR(p.priceINR)}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
